/**
 * The capability registry — every door in CIDER, with its module, view, section, row scope
 * and field rules. Source: `program/Contracts` (the model, the lanes, the scopes) and the
 * capability names on `program/Build Plan`. A route, a screen or a machine that names a
 * capability not in this list does not compile. `tools/coverage.ts` checks every id on the
 * canvas is here.
 */
import type { Capability } from './registry.js';

const C = (c: Capability): Capability => c;

export const CAPABILITIES: readonly Capability[] = [
  // ---- MONEY -----------------------------------------------------------------------------
  C({
    // ADR 0028 (James, Oct 12): Where we stand, Money's home — read-only.
    id: 'money.today.read',
    module: 'money',
    view: 'money',
    section: 'Today',
    what: 'Read Where we stand: cash tonight, cards today, bills due this week, seed not back, and what needs a person.',
    rowScope: 'all',
    askRole: 'finance_director',
  }),
  C({
    id: 'finance.approve',
    module: 'money',
    view: 'decide',
    section: 'Decide',
    what: 'Approve any purchase, expense or bill — one director, any amount.',
    rowScope: 'all',
    askRole: 'director',
  }),
  C({
    id: 'finance.sign',
    module: 'money',
    view: 'money',
    section: 'Spend · Pay',
    what: 'Sign a check. Two distinct directors on every one.',
    rowScope: 'all',
    placed: 'Money → Spend · Pay → Print checks (Money Part 2)',
    askRole: 'director',
  }),
  C({
    id: 'finance.post',
    module: 'money',
    view: 'money',
    section: 'Spend · Pay',
    what: 'Post a journal entry, a bill, a reversal.',
    rowScope: 'all',
    askRole: 'finance_director',
  }),
  C({
    id: 'finance.pay',
    module: 'money',
    view: 'money',
    section: 'Spend · Pay',
    what: 'Print a check, void one with a reason, reissue after a jam.',
    rowScope: 'all',
    askRole: 'finance_director',
  }),
  C({
    id: 'finance.import',
    module: 'money',
    view: 'money',
    section: 'Close · Report',
    what: 'Import the 23 QuickBooks years. Once.',
    rowScope: 'all',
    askRole: 'finance_director',
  }),
  C({
    id: 'finance.reconcile',
    module: 'money',
    view: 'money',
    section: 'Cash · Bank',
    what: 'Import bank lines, match many-to-one, reconcile a month.',
    rowScope: 'all',
    askRole: 'finance_director',
  }),
  C({
    id: 'finance.close',
    module: 'money',
    view: 'money',
    section: 'Close · Report',
    what: 'Lock a period; reopen with a reason; year-end package.',
    rowScope: 'all',
    askRole: 'finance_director',
  }),
  C({
    id: 'finance.view_bank',
    module: 'money',
    view: 'money',
    section: 'Cash · Bank',
    what: 'See bank balances and lines.',
    rowScope: 'all',
    askRole: 'director',
  }),
  C({
    id: 'finance.view',
    module: 'money',
    view: 'money',
    section: 'Ask · Approve',
    what: 'See budget lines and spend.',
    rowScope: 'all',
    placed: 'Money → every read screen (Money Part 1)',
    askRole: 'finance_director',
  }),
  C({
    id: 'finance.view:my_area',
    module: 'chairs',
    view: 'chairs',
    section: 'My money',
    what: 'See my area’s line and my spend against it. Never edit it.',
    rowScope: 'my_areas',
    askRole: 'finance_director',
  }),
  C({
    // ADR 0030 (James, Oct 14): a chair proposes a change to a line of their own area, with a
    // reason. It writes a proposal and changes no number; every one goes to the queue.
    id: 'finance.budget.propose:my_area',
    module: 'money',
    view: 'chairs',
    section: 'My money',
    what: 'Ask for a change to a line of my area’s budget, with a sentence. It goes to the review queue; it never changes a number.',
    rowScope: 'my_areas',
    askRole: 'finance_director',
  }),
  C({
    // ADR 0034 (James, Oct 14): Forms — make a small form, open it, read its answers.
    id: 'form.make',
    module: 'marketing',
    view: 'marketing',
    section: 'Public website',
    screens: ['frm01'],
    what: 'Make a small form — six kinds of question, a door, an end date — open it, and read its answers as a worklist.',
    rowScope: 'all',
    askRole: 'volunteer_chair',
  }),
  C({
    // ADR 0034: a form to every volunteer, family or sponsor waits for a director's yes.
    id: 'form.approve',
    module: 'marketing',
    view: 'marketing',
    section: 'Public website',
    screens: ['frm01'],
    what: 'Say yes to a form that goes to everyone.',
    rowScope: 'all',
    askRole: 'director',
  }),
  C({
    id: 'finance.budget.edit',
    module: 'money',
    view: 'money',
    section: 'Ask · Approve',
    what: 'Edit a draft budget line; lock the budget; amend with two names.',
    rowScope: 'all',
    placed: 'Money → Ask · Approve → Committed → Lines & owners (Money Part 3)',
    askRole: 'director',
  }),
  C({
    id: 'finance.countroom',
    module: 'money',
    view: 'money',
    section: 'Cash · Bank',
    what: 'Issue seed bags, count with two names, seal the day, deposit slip.',
    rowScope: 'year',
    askRole: 'count_room_chair',
  }),
  C({
    id: 'finance.pickup',
    module: 'money',
    view: 'money',
    section: 'Cash · Bank',
    what: 'Log a cash pickup and a change run.',
    rowScope: 'year',
    placed: 'Money → Cash · Bank → Count room (Money Part 4b)',
    askRole: 'cash_change_chair',
  }),
  C({
    id: 'finance.deposit',
    module: 'money',
    view: 'money',
    section: 'Cash · Bank',
    what: 'Draw the seed from the bank; deposit the sealed day.',
    rowScope: 'all',
    placed: 'Money → Cash · Bank → Count room → Deposit builder · Day seal (Money Part 4b)',
    askRole: 'director',
  }),
  C({
    id: 'finance.distribute',
    module: 'money',
    view: 'money',
    section: 'Close · Report',
    what: 'Compute and draft what is left to give; post the holdback.',
    rowScope: 'all',
    placed: 'Money → Close · Report → Distributions (Money Part 5)',
    askRole: 'finance_director',
  }),
  C({
    id: 'finance.distribute.approve',
    module: 'money',
    view: 'decide',
    section: 'Decide',
    what: 'One of the two approvals on a distribution.',
    rowScope: 'all',
    placed: 'Money → Close · Report → Distributions (Money Part 5)',
    askRole: 'director',
  }),
  C({
    id: 'finance.order',
    module: 'money',
    view: 'money',
    section: 'Spend · Pay',
    what: 'Raise a numbered purchase order; cancel with a reason.',
    rowScope: 'all',
    placed: 'Money → Spend · Pay → Purchase orders (Money Part 2)',
    askRole: 'finance_director',
  }),
  C({
    id: 'money.receivable',
    module: 'money',
    view: 'money',
    section: 'Sell · Count',
    what: 'Money owed to us: raise a numbered invoice with its due date, post it, record what comes in, void one with a reason, chase an overdue one. Paid comes from the ledger, never from a tick (Sponsors Part 5).',
    rowScope: 'all',
    placed: 'Money → Sell · Count → Receivables',
    askRole: 'finance_director',
  }),
  C({
    id: 'finance.refund',
    module: 'commerce',
    view: 'money',
    section: 'Sell · Count',
    what: 'Record a refund with a reason. Never edits the payment.',
    rowScope: 'all',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'pii.view_vendor_tax',
    module: 'money',
    view: 'money',
    section: 'Spend · Pay',
    what: 'Reveal a vendor’s tax number. Logged.',
    rowScope: 'all',
    fields: { 'vendor.tax_number': 'read' },
    askRole: 'finance_director',
  }),
  C({
    id: 'square.mapping.manage',
    module: 'money',
    view: 'money',
    section: 'Sell · Count',
    what: 'Map our items to Square’s; the pre-festival change list.',
    rowScope: 'all',
    placed: 'Money → Sell · Count → Item mapping (Money Part 3)',
    askRole: 'square_admin',
  }),
  C({
    id: 'square.catalog.read',
    module: 'money',
    view: 'money',
    section: 'Sell · Count',
    what: 'Read Square’s catalog into the mapping; name one of ours. The Square admin and the finance director.',
    rowScope: 'all',
    placed: 'Money → Sell · Count → Item mapping (Money Part 3)',
    askRole: 'square_admin',
  }),

  // ---- VOLUNTEERS ------------------------------------------------------------------------
  C({
    id: 'volunteer.view_roster:my_areas',
    module: 'volunteers',
    view: 'chairs',
    section: 'My roster',
    what: 'See who is signed up and who is here, for my areas only.',
    rowScope: 'my_areas',
    fields: { 'person.emergency_contact': 'hidden' },
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'volunteer.checkin',
    module: 'volunteers',
    view: 'chairs',
    section: 'Check-in / out',
    what: 'Check a person in or out where they are standing.',
    rowScope: 'my_areas',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'volunteer.edit_signup:my_areas',
    module: 'volunteers',
    view: 'chairs',
    section: 'My roster',
    what: 'Fix a signup in my areas.',
    rowScope: 'my_areas',
    askRole: 'recruiting_chair',
  }),
  C({
    id: 'volunteer.edit_signup:all_areas',
    module: 'volunteers',
    view: 'volunteers',
    section: 'Signups',
    what: 'Fix any signup in any area; cancel after the cut-off; void a past day with a reason.',
    rowScope: 'all',
    askRole: 'recruiting_chair',
  }),
  C({
    id: 'volunteer.verify_contact',
    module: 'volunteers',
    view: 'volunteers',
    section: 'Reminders & verification',
    what: 'The bounce and unconfirmed worklists; mark an address good with a reason.',
    rowScope: 'all',
    askRole: 'volunteer_chair',
  }),
  C({
    // ADR 0027 (James, Oct 12): Volunteers' Year settings — read-only, no Save.
    id: 'volunteer.settings.read',
    module: 'volunteers',
    view: 'volunteers',
    section: 'Settings (year)',
    what: 'Read the named values Volunteers keeps for the year: the cancel cut-off, the reminder cadence, the day-of text, the signup states.',
    rowScope: 'all',
    askRole: 'volunteer_chair',
  }),
  C({
    // ADR 0029 (James, Oct 13): Service hours — sign the letters, see a group's hours, a person's
    // letters; supersede with a reason. Hours are never typed.
    id: 'volunteer.service_hours',
    module: 'volunteers',
    view: 'volunteers',
    // ADR 0033 (James, Oct 14): Service hours' three screens sit in Families & commitments.
    section: 'Families & commitments',
    screens: ['sh-queue', 'sh-groups', 'sh-person'],
    what: 'Sign service-hours letters from check-outs, read a group leader’s list and a person’s letters, and supersede a letter with a reason.',
    rowScope: 'all',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'volunteer.compliance.record',
    module: 'volunteers',
    view: 'volunteers',
    section: 'Recruiting & prospects',
    what: 'Send names to the school; record its answer. CIDER never runs a check.',
    rowScope: 'all',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'shift.propose',
    module: 'volunteers',
    view: 'chairs',
    section: 'My open shifts',
    what: 'Propose a shift for my area. Invisible until approved.',
    rowScope: 'my_areas',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'shift.approve_proposal',
    module: 'volunteers',
    view: 'volunteers',
    section: 'Shifts',
    what: 'Approve or decline a proposal; build and publish shifts; cut capacity.',
    rowScope: 'all',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'season.import',
    module: 'volunteers',
    view: 'admin',
    section: 'People & roles',
    what: 'Bring a 1.0 volunteer season in — shifts, signups, the day, the links, the reminders and the curve — with the card, a re-run that changes nothing, and Undo this run.',
    rowScope: 'all',
    placed: 'Festival Admin → People & roles → Import (Volunteers Part 1, A)',
    askRole: 'director',
  }),
  C({
    id: 'roster.print',
    module: 'volunteers',
    view: 'chairs',
    section: 'My roster',
    what: 'Print the day list for my area.',
    rowScope: 'my_areas',
    askRole: 'volunteer_chair',
  }),
  // ---- PEOPLE (People Part 1: nobody exists twice) ----------------------------------------
  C({
    id: 'people.view',
    module: 'people',
    view: 'admin',
    section: 'People & roles',
    what: 'The people list and a person’s record: keys and their states, aliases, household, history.',
    rowScope: 'all',
    placed: 'Festival Admin → People & roles → People · Identity (defect #47)',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'people.edit',
    module: 'people',
    view: 'admin',
    section: 'People & roles',
    what: 'Add or change a person, a key, a household, an organization — through the identity ladder, never a second row for the same human.',
    rowScope: 'all',
    placed: 'Festival Admin → People & roles → People (People Part 1)',
    askRole: 'volunteer_chair',
  }),
  C({
    // ADR 0060 (James, Oct 22): Unmatched workers — This is … · New household · Not ours · reopen.
    id: 'people.unmatched.resolve',
    module: 'people',
    view: 'admin',
    section: 'People & roles',
    what: 'Say who a worker in no family is: This is … a family (the shifts already worked are credited), a new household, or not ours; reopen one from Show inactive.',
    rowScope: 'all',
    // The row shows the phone and the email it has, so whoever answers can tell who it is.
    fields: { 'person.phone': 'read', 'person.email': 'read' },
    placed: 'Festival Admin → People & roles → Unmatched workers (Rebuild 48 #unmatched)',
    askRole: 'volunteer_chair',
  }),
  C({
    // ADR 0060: the same three on the chair's roster, for the areas the chair holds.
    id: 'people.unmatched.resolve:my_areas',
    module: 'people',
    view: 'chairs',
    section: 'My roster',
    what: 'Who is this? — on my roster, a worker in no family: This is … · New household · Not ours, for my areas only.',
    rowScope: 'my_areas',
    fields: { 'person.phone': 'hidden', 'person.email': 'hidden' },
    placed: 'Chairs → My roster (Rebuild 48 #unmatched)',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'people.merge',
    module: 'people',
    view: 'admin',
    section: 'People & roles',
    what: 'Merge two persons: pick the survivor; the loser becomes a tombstone that redirects. Reversible for thirty days.',
    rowScope: 'all',
    placed: 'Festival Admin → People & roles → Identity · a person (defect #47)',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'people.import',
    module: 'people',
    view: 'admin',
    section: 'People & roles',
    what: 'Bring the 1.0 people snapshot in, with the reconciliation card.',
    rowScope: 'all',
    placed: 'Festival Admin → People & roles → Import (defect #48)',
    askRole: 'director',
  }),
  C({
    id: 'people.erase',
    module: 'people',
    view: 'admin',
    section: 'People & roles',
    what: 'A deletion request on a person: a director asks, thirty days hold, then the personal fields go and the ledger references stay (DATA_CLASSIFICATION). Cancel inside the hold.',
    rowScope: 'all',
    askRole: 'director',
    placed: 'Festival Admin → People & roles → People → a person · Offboarding (People Part 5)',
  }),
  C({
    id: 'people.compliance.view',
    module: 'people',
    view: 'admin',
    section: 'People & roles',
    what: 'Who is missing a check: the compliance worklist, states and dates only, never a document.',
    rowScope: 'all',
    askRole: 'volunteer_chair',
    placed: 'Festival Admin → People & roles → Identity → Missing a check (defect #65)',
  }),
  C({
    id: 'people.compliance.record',
    module: 'people',
    view: 'admin',
    section: 'People & roles',
    what: 'The volunteer chairs record a result on a chair’s packet: a background or driving check’s answer, ID seen, a kit item; and a driving license’s masked read.',
    rowScope: 'all',
    askRole: 'volunteer_chair',
    placed:
      'Festival Admin → People & roles → Chairs → a chair’s packet (People Part 7; canvas defect #84)',
  }),
  C({
    id: 'people.export_contact',
    module: 'people',
    view: 'admin',
    section: 'People & roles',
    what: 'Export with the contact columns — phone, email, emergency contact. Otherwise an export strips them. The one export door (DATA_CLASSIFICATION).',
    rowScope: 'all',
    fields: { 'person.phone': 'read', 'person.email': 'read', 'person.emergency_contact': 'read' },
    placed: 'Festival Admin → People & roles → People → Export (People Part 1)',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'family.merge',
    module: 'people',
    view: 'volunteers',
    section: 'Families & commitments',
    // ADR 0033: Service hours shares the section; this door opens the families' screens only.
    screens: ['families', 'cm09', 'cm12', 'cm13', 'cm08', 'cm14', 'declared'],
    what: 'Re-match a student link; merge duplicates. Never delete.',
    rowScope: 'all',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'commitment.credit.run',
    module: 'volunteers',
    view: 'volunteers',
    section: 'Families & commitments',
    // ADR 0033: Service hours shares the section; this door opens the families' screens only.
    screens: ['families', 'cm09', 'cm12', 'cm13', 'cm08', 'cm14', 'declared'],
    what: 'Compute, diff and post a credit run. Once.',
    rowScope: 'year',
    askRole: 'volunteer_chair',
  }),
  C({
    // Doors in Part 1: what "Your family's year" wrote — the intent, never money or credit.
    id: 'commitment.intent.view',
    module: 'volunteers',
    view: 'volunteers',
    section: 'Families & commitments',
    // ADR 0033: Service hours shares the section; this door opens the families' screens only.
    screens: ['families', 'cm09', 'cm12', 'cm13', 'cm08', 'cm14', 'declared'],
    what: 'Declared for the year: each family’s intent and the four worklists; tick “reached out” with a name and a date.',
    rowScope: 'year',
    askRole: 'volunteer_chair',
    placed: 'Volunteers → Families & commitments → Declared (canvas defect #99)',
  }),
  C({
    // ADR 0029 (James, Oct 13): the school's ledger. School families only — the family's name,
    // the parents' names, the hours and the four steps; never a phone, an email, an emergency
    // contact, a date of birth, a waiver or a note (no `fields`: the defaults keep them hidden).
    // It never changes an hour.
    id: 'commitment.ledger',
    module: 'volunteers',
    view: 'volunteers',
    section: 'School ledger',
    what: 'Read every school family’s hours against the year’s units and the four steps after the festival: the shortfall letter, the buy-out, an appeal, the hand-off to tuition.',
    rowScope: 'year',
    askRole: 'director',
  }),
  C({
    id: 'commitment.settings:year',
    module: 'admin',
    view: 'admin',
    section: 'Year settings',
    what: 'Set and lock the year’s commitment settings.',
    rowScope: 'year',
    askRole: 'director',
  }),
  C({
    id: 'commitment.override',
    module: 'volunteers',
    view: 'decide',
    section: 'Decide',
    what: 'Excuse a commitment; override a compliance block with a reason; choose the family for a dispute the import could not place.',
    rowScope: 'all',
    askRole: 'director',
  }),
  C({
    id: 'booth.device.enroll',
    module: 'admin',
    view: 'volunteers',
    section: 'Check-in booths',
    what: 'Enroll, unlock, rotate and revoke a booth device.',
    rowScope: 'all',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'booth.operate',
    module: 'volunteers',
    view: 'volunteers',
    section: 'Check-in booths',
    what: 'Check in, check out, walk-up, reprint, void today. From an enrolled device.',
    rowScope: 'device',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'debrief.collect',
    module: 'admin',
    view: 'admin',
    section: 'Debrief & next-year plan',
    what: 'Send the six questions; nudge; record decisions and plan items.',
    rowScope: 'year',
    askRole: 'volunteer_chair',
  }),

  // ---- CHAIRS ----------------------------------------------------------------------------
  C({
    // ADR 0032 (James, Oct 13): I'm here · I've left — a role holder's own presence on site.
    id: 'self.presence',
    module: 'volunteers',
    view: 'chairs',
    section: 'Check-in / out',
    what: 'Say I’m here and I’ve left on a festival day: on site, on call, the hours on my own record.',
    rowScope: 'own',
    askRole: 'volunteer_chair',
    placed: 'The chair app → Today (Rebuild 32, ch15)',
  }),
  C({
    id: 'self.onboarding',
    module: 'admin',
    view: 'chairs',
    section: 'More',
    what: 'My own yearly packet: read it, answer it, sign it. Nobody else’s.',
    rowScope: 'own',
    askRole: 'volunteer_chair',
    placed: 'The chair app → Your packet (People Part 7; canvas defect #84)',
  }),
  C({
    id: 'area.brief.edit',
    module: 'chairs',
    view: 'chairs',
    section: 'More',
    what: 'What to bring, expect and wear — my area.',
    rowScope: 'my_areas',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'supply.request',
    module: 'festival_ops',
    view: 'chairs',
    section: 'Requests',
    what: 'Ask for a thing; the spending rule shows on the ask.',
    rowScope: 'my_areas',
    askRole: 'dispatch',
  }),
  C({
    id: 'expense.submit',
    module: 'money',
    view: 'chairs',
    section: 'Requests',
    what: 'Send in something I paid for out of my own pocket: a photo of the receipt and one line. The amount is optional and stays my figure until the finance director reads the receipt (Money Part 4).',
    rowScope: 'own',
    askRole: 'finance_director',
  }),
  C({
    // ADR 0036 (Rebuild 34/35, ch04po): "they'll bill us" from the chair app — vendor · line ·
    // what · about how much. Open on filing, numbered, a director's yes once.
    id: 'purchase.open:my_area',
    module: 'money',
    view: 'chairs',
    section: 'Requests',
    what: 'Open a PO for my area: vendor · line · what · about how much. The number goes to the vendor; a director approves it once.',
    rowScope: 'my_areas',
    askRole: 'finance_director',
  }),
  C({
    id: 'supply.fulfill',
    module: 'festival_ops',
    view: 'ops',
    section: 'Supplies & vendors',
    what: 'Acknowledge, carry, deliver or refuse a request with a reason; receive goods.',
    rowScope: 'year',
    askRole: 'dispatch',
  }),
  C({
    id: 'menu.edit:my_area',
    module: 'marketing',
    view: 'chairs',
    section: 'More',
    what: 'Mark my area’s items sold out; edit my board’s prices before proofing.',
    rowScope: 'my_areas',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'menu.copy.approve',
    module: 'marketing',
    view: 'marketing',
    section: 'Signage & menu boards',
    what: 'Approve menu copy; proof and publish the boards.',
    rowScope: 'all',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'radio.custody',
    module: 'festival_ops',
    view: 'chairs',
    section: 'More',
    what: 'Sign a radio or key out to me and back.',
    rowScope: 'own',
    askRole: 'dispatch',
  }),
  C({
    id: 'safety.view',
    module: 'festival_ops',
    view: 'chairs',
    section: 'Safety card',
    what: 'Read my area’s safety card.',
    rowScope: 'my_areas',
    askRole: 'dispatch',
  }),
  C({
    id: 'msg.send:chairs_directors',
    module: 'comms',
    view: 'chairs',
    section: 'More',
    what: 'Message the other chairs and the directors. Never the public.',
    rowScope: 'own',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'closeout.sign',
    module: 'chairs',
    view: 'chairs',
    section: 'More',
    what: 'Sign my area’s closeout and answer the debrief.',
    rowScope: 'my_areas',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'closeout.open',
    module: 'money',
    view: 'money',
    section: 'Close · Report',
    what: 'Tick a closeout line; reopen from the top with a reason.',
    rowScope: 'year',
    askRole: 'director',
  }),
  C({
    id: 'chair.pipeline',
    module: 'chairs',
    view: 'volunteers',
    section: 'Recruiting & prospects',
    what: 'Add a prospect, send the ask, name a successor.',
    rowScope: 'all',
    askRole: 'director',
  }),
  C({
    id: 'ride.view_redemption',
    module: 'commerce',
    view: 'chairs',
    section: 'My money',
    what: 'See bracelets redeemed and left.',
    rowScope: 'my_areas',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'ride.stock.count',
    module: 'festival_ops',
    view: 'chairs',
    section: 'More',
    what: 'Count the physical bracelet stock, two names.',
    rowScope: 'my_areas',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'ride.presale.manage',
    module: 'commerce',
    view: 'marketing',
    section: 'Tickets & presale',
    what: 'Open and close the presale; the price list.',
    rowScope: 'all',
    askRole: 'square_admin',
  }),
  C({
    id: 'raffle.operate',
    module: 'marketing',
    view: 'marketing',
    section: 'Raffles & baskets',
    what: 'Issue books to a name, take them back with the cash, two names.',
    rowScope: 'my_areas',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'raffle.draw',
    module: 'marketing',
    view: 'marketing',
    section: 'Raffles & baskets',
    what: 'Draw, with a witness named. Never reopen.',
    rowScope: 'year',
    askRole: 'director',
  }),
  C({
    id: 'basket.intake',
    module: 'marketing',
    view: 'marketing',
    section: 'Raffles & baskets',
    what: 'Catalog a basket with a photo and the donor’s value; thank the donor.',
    rowScope: 'year',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'door.admit',
    module: 'commerce',
    view: 'ops',
    section: 'Rides day-of',
    what: 'Scan a ticket at the hall door or a bracelet at the booth.',
    rowScope: 'device',
    askRole: 'volunteer_chair',
  }),
  C({
    // R5 (James, Oct 22): the chair price book is a director's to approve — two hands, never the
    // person who last changed a chair price.
    id: 'commerce.pricebook.approve',
    module: 'commerce',
    view: 'marketing',
    section: 'Tickets & presale',
    what: 'Approve the chair store’s price book for the year; never the person who last changed a chair price.',
    rowScope: 'all',
    askRole: 'director',
  }),
  C({
    id: 'commerce.chair.sell',
    module: 'commerce',
    view: 'marketing',
    section: 'Tickets & presale',
    what: 'Record a chair-store sale at the approved chair prices; the order is tied to the seller.',
    rowScope: 'all',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'ticket.comp',
    module: 'commerce',
    view: 'marketing',
    section: 'Tickets & presale',
    what: 'Comp a ticket, name and reason required; move one person between seatings.',
    rowScope: 'all',
    askRole: 'volunteer_chair',
  }),

  // ---- FESTIVAL OPS ----------------------------------------------------------------------
  C({
    id: 'wt.operate',
    module: 'festival_ops',
    view: 'ops',
    section: 'Watchtower',
    what: 'Run the desk and the field app.',
    rowScope: 'year',
    askRole: 'dispatch',
  }),
  C({
    id: 'wt.wall.view',
    module: 'festival_ops',
    view: 'ops',
    section: 'Watchtower',
    what: 'The directors’ wall.',
    rowScope: 'all',
    askRole: 'director',
  }),
  C({
    id: 'wt.incident.open',
    module: 'festival_ops',
    view: 'ops',
    section: 'Incidents & weather',
    what: 'Open an incident from the field app.',
    rowScope: 'year',
    askRole: 'dispatch',
  }),
  C({
    id: 'wt.incident.close',
    module: 'festival_ops',
    view: 'ops',
    section: 'Incidents & weather',
    what: 'Assign; close with a report. Never without one.',
    rowScope: 'year',
    askRole: 'dispatch',
  }),
  C({
    id: 'wt.emergency.set',
    module: 'festival_ops',
    view: 'ops',
    section: 'Incidents & weather',
    what: 'Set a hold or an evacuation, or lift it. Lifting early carries a name.',
    rowScope: 'year',
    askRole: 'director',
  }),
  C({
    // Emails g21 (James, Oct 18): the lightning hold and all-clear texts, pressed by a director or
    // Dispatch — the text only; the hold itself stays the director's.
    id: 'wt.weather.text',
    module: 'festival_ops',
    view: 'ops',
    section: 'Incidents & weather',
    what: 'Text the lightning hold or the all-clear to everyone on site and on shift; chairs get the shelter roll.',
    rowScope: 'year',
    placed: 'Festival Ops → Incidents & weather → Weather console (Emails g21)',
    askRole: 'director',
  }),
  C({
    // The ticket window's lookup (James, Oct 17 — defect 266's second half): a person, not a
    // device, finds a bracelet order and hands its bracelets over, with their own name recorded.
    id: 'ride.window.redeem',
    module: 'commerce',
    view: 'ops',
    section: 'Gates, doors & keys',
    what: 'Find a bracelet order at the ticket window and hand its bracelets over.',
    rowScope: 'all',
    askRole: 'volunteer_chair',
    placed: 'Festival Ops → Gates, doors & keys → The ticket window (tk04; defect 266)',
  }),
  C({
    id: 'wt.gate.report',
    module: 'festival_ops',
    view: 'ops',
    section: 'Gates, doors & keys',
    what: 'Open or close a gate with a direction; the hourly count.',
    rowScope: 'year',
    askRole: 'dispatch',
  }),
  C({
    id: 'wt.radio.custody',
    module: 'festival_ops',
    view: 'ops',
    section: 'Gates, doors & keys',
    what: 'The one custody list for radios and keys.',
    rowScope: 'year',
    askRole: 'dispatch',
  }),
  C({
    id: 'wt.readiness.walk',
    module: 'festival_ops',
    view: 'ops',
    section: 'Readiness',
    what: 'Walk an area’s readiness; open with flags.',
    rowScope: 'year',
    askRole: 'dispatch',
  }),

  C({
    id: 'ops.grounds.edit',
    module: 'festival_ops',
    view: 'ops',
    section: 'Grounds & power',
    what: 'Place site elements, circuits, the safety walk, mutual aid.',
    rowScope: 'year',
    askRole: 'dispatch',
    placed: 'Festival Ops → Grounds & power (the rail section’s one door)',
  }),

  // ---- MARKETING -------------------------------------------------------------------------
  C({
    id: 'signage.screen.manage',
    module: 'marketing',
    view: 'marketing',
    section: 'Signage & menu boards',
    what: 'The screen registry; a dark screen named.',
    rowScope: 'all',
    askRole: 'dispatch',
  }),
  C({
    id: 'signage.proof.render',
    module: 'marketing',
    view: 'marketing',
    section: 'Signage & menu boards',
    what: 'Render every screen’s next frame, both orientations; publish a playlist version.',
    rowScope: 'all',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'site.edit',
    module: 'marketing',
    view: 'marketing',
    section: 'Public website',
    what: 'Draft and schedule pages and blocks; tag and crop media; redirects.',
    rowScope: 'all',
    askRole: 'website_social',
  }),
  C({
    id: 'site.publish',
    module: 'marketing',
    view: 'marketing',
    section: 'Public website',
    what: 'Publish a version; roll back. A director act.',
    rowScope: 'all',
    askRole: 'director',
  }),
  // ---- ORGANIZATIONS (Sponsors Part 1; James's rulings Sep 21: their own doors, not People's; the
  // sponsorship lead owns them — ADR 0010) --
  C({
    id: 'organization.view',
    module: 'people',
    view: 'marketing',
    section: 'Sponsors & recognition',
    what: 'The organizations list and a record: contacts, other names, years sponsored, notes, who did what.',
    rowScope: 'all',
    placed: 'Marketing → Sponsors & recognition → Organizations (defect #89)',
    askRole: 'sponsorship_lead',
  }),
  C({
    id: 'organization.edit',
    module: 'people',
    view: 'marketing',
    section: 'Sponsors & recognition',
    what: 'Add or change an organization, a name it is known by, a contact at it, a note — through the ladder, never a second row for the same business.',
    rowScope: 'all',
    placed: 'Marketing → Sponsors & recognition → Organizations (defect #89)',
    askRole: 'sponsorship_lead',
  }),
  C({
    id: 'organization.merge',
    module: 'people',
    view: 'marketing',
    section: 'Sponsors & recognition',
    what: 'Merge two organizations: pick the survivor; the loser becomes a tombstone that redirects. Undo puts them back.',
    rowScope: 'all',
    placed: 'Marketing → Sponsors & recognition → an organization (defect #89)',
    askRole: 'sponsorship_lead',
  }),
  C({
    id: 'sponsor.manage',
    module: 'marketing',
    view: 'marketing',
    section: 'Sponsors & recognition',
    what: 'Tiers, pledges as a ledger, recognition slots, artwork check and approval, thank-yous.',
    rowScope: 'all',
    askRole: 'sponsorship_lead',
  }),
  C({
    id: 'sponsor.artwork.approve',
    module: 'marketing',
    view: 'marketing',
    section: 'Sponsors & recognition',
    what: 'Mark a logo version approved, or send it back with the sentence for the sponsor. The sponsorship lead (ADR 0010); a director too.',
    rowScope: 'all',
    placed:
      'Marketing → Sponsors & recognition → a sponsor record, LOGO VAULT (Part 4 adds the queue; defect #95)',
    askRole: 'sponsorship_lead',
  }),
  C({
    id: 'sponsor.artwork.view',
    module: 'marketing',
    view: 'marketing',
    section: 'Sponsors & recognition',
    what: 'The artwork queue and where a sponsor appears: logos, files, placements, the banner order. No contact, no pledge, no payment, no person — the designer’s two screens (Sponsors Part 4).',
    rowScope: 'all',
    placed: 'Marketing → Sponsors & recognition → Artwork (sp03) and Recognition (sp04)',
    screens: ['sp03', 'sp04'],
    askRole: 'sponsorship_lead',
  }),
  C({
    id: 'sponsor.artwork.check',
    module: 'marketing',
    view: 'marketing',
    section: 'Sponsors & recognition',
    what: 'Check a logo version against the list — format, size, safe area, background — and say it is checked. Checking is not approving: a director or the sponsorship lead approves.',
    rowScope: 'all',
    placed: 'Marketing → Sponsors & recognition → Artwork (sp03)',
    screens: ['sp03', 'sp04'],
    askRole: 'sponsorship_lead',
  }),
  C({
    id: 'sponsor.gift',
    module: 'marketing',
    view: 'marketing',
    section: 'Sponsors & recognition',
    what: 'Gifts in kind: record an offer, accept it or decline it with a reason, print the receipt. The donor sets the value; it never joins the money figure.',
    rowScope: 'all',
    placed: 'Marketing → Sponsors & recognition → Gifts in kind (sp06)',
    askRole: 'sponsorship_lead',
  }),
  C({
    id: 'sponsor.gift.receive',
    module: 'marketing',
    view: 'chairs',
    section: 'Requests',
    what: 'Tick a gift in kind as received in my area — the chair who takes delivery, nobody else (canvas defect #117).',
    rowScope: 'my_areas',
    placed: 'Chairs → Requests → Gifts on the way',
    askRole: 'sponsorship_lead',
  }),
  C({
    id: 'sponsor.ask',
    module: 'marketing',
    view: 'marketing',
    section: 'Sponsors & recognition',
    what: 'The January asks: build the list, own them, send the letter and the email, record the answer. Invoice from the money side.',
    rowScope: 'all',
    placed: 'Marketing → Sponsors & recognition → Asks (sp05)',
    askRole: 'sponsorship_lead',
  }),
  C({
    id: 'sponsor.ask.mine',
    module: 'marketing',
    view: 'marketing',
    section: 'Sponsors & recognition',
    what: 'The asks I own, and only those: see them, write to them, record what was said (Sponsors Part 3). A chair never sees another owner’s ask.',
    rowScope: 'own',
    placed: 'Decide → Asks I own; Marketing → Sponsors & recognition → Asks, mine',
    askRole: 'sponsorship_lead',
  }),

  // ---- COMMS -----------------------------------------------------------------------------
  C({
    id: 'comms.send_email',
    module: 'comms',
    view: 'admin',
    section: 'Messages & deliverability',
    what: 'Preview and send a run. One sender.',
    rowScope: 'all',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'comms.send_sms',
    module: 'comms',
    view: 'admin',
    section: 'Messages & deliverability',
    what: 'The three text purposes. Urgent is director-only.',
    rowScope: 'all',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'comms.push',
    module: 'comms',
    view: 'ops',
    section: 'Watchtower',
    what: 'Push an announcement. A hold push is director-only.',
    rowScope: 'year',
    askRole: 'dispatch',
  }),
  C({
    id: 'comms.queue.retry',
    module: 'comms',
    view: 'admin',
    section: 'Messages & deliverability',
    what: 'Retry the failed queue; read delivery truth.',
    rowScope: 'all',
    askRole: 'volunteer_chair',
  }),

  C({
    id: 'comms.newsletter',
    module: 'comms',
    view: 'marketing',
    section: 'Announcements & templates',
    what: 'Build a consented segment; compose from the template family.',
    rowScope: 'all',
    askRole: 'website_social',
    placed: 'Marketing → Announcements & templates (the rail section’s one door)',
  }),

  C({
    id: 'survey.read',
    module: 'marketing',
    view: 'marketing',
    section: 'Guest survey',
    what: 'Read the guest survey: the answers and the debrief they feed.',
    rowScope: 'year',
    askRole: 'director',
    placed: 'Marketing → Guest survey (the rail section’s one door)',
  }),

  // ---- FESTIVAL ADMIN -----------------------------------------------------------------
  C({
    id: 'admin.grant',
    module: 'admin',
    view: 'admin',
    section: 'People & roles',
    what: 'Grant or revoke a role assignment. Granter ≠ grantee.',
    rowScope: 'all',
    placed:
      'Festival Admin → People & roles → Roles · Chairs · Successors · Chair record (People Part 2)',
    askRole: 'director',
  }),
  C({
    id: 'admin.invite',
    module: 'admin',
    view: 'admin',
    section: 'People & roles',
    what: 'Invite a person to set up sign-in. One use, two weeks.',
    rowScope: 'all',
    placed: 'Festival Admin → People & roles → Invitations (People Part 5)',
    askRole: 'director',
  }),
  C({
    id: 'admin.invite:area_chairs',
    module: 'admin',
    view: 'admin',
    section: 'People & roles',
    what: 'Invite an area chair.',
    rowScope: 'all',
    placed: 'Festival Admin → People & roles → Invitations (People Part 5)',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'admin.session.revoke',
    module: 'admin',
    view: 'admin',
    section: 'People & roles',
    what: 'Sign a person out of one device or all.',
    rowScope: 'all',
    placed: 'Festival Admin → System → Access issues (defect #59)',
    askRole: 'director',
  }),
  C({
    id: 'admin.second_factor.reset',
    module: 'admin',
    view: 'admin',
    section: 'People & roles',
    what: 'Recover another person’s second factor — the platform admin, audited and flagged.',
    rowScope: 'all',
    placed: 'Festival Admin → System → Access issues (defect #20)',
    askRole: 'platform_admin',
  }),
  C({
    id: 'admin.view_as',
    module: 'admin',
    view: 'admin',
    section: 'People & roles',
    what: 'View as a chair. Read-only, one hour, banner, audited and visible to the chair.',
    rowScope: 'all',
    placed: 'The shell → View as a chair (People Part 2)',
    askRole: 'director',
  }),
  C({
    id: 'admin.settings:year',
    module: 'admin',
    view: 'admin',
    section: 'Year settings',
    what: 'Prices, cut-offs, tiers, requirements by role. Locked at season open.',
    rowScope: 'year',
    placed: 'Festival Admin → Year settings → Year settings (defect #53)',
    askRole: 'director',
  }),
  C({
    id: 'admin.year.open',
    module: 'admin',
    view: 'admin',
    section: 'Year settings',
    what: 'Open next year: one button, one preview.',
    rowScope: 'all',
    placed: 'Festival Admin → Year settings → Go live · Next year (People Part 3)',
    askRole: 'director',
  }),
  C({
    // Codex review #1, finding 4 (James, Oct 21): the live stream and the bell were "public" to
    // the guard and checked a person inside the handler. They are a door like every other.
    id: 'live.read',
    module: 'admin',
    view: 'decide',
    section: 'Decide',
    what: 'Hear my own screens change as it happens, and read my own bell: its list, done, and the phones it rings.',
    rowScope: 'own',
    placed:
      'Every signed-in screen: the live stream (GET /events) and the bell (comms notifications and push)',
    askRole: 'director',
  }),
  C({
    // P28 (M7): Jump to anything was public, checked inside the handler; now it is a door every
    // signed-in role holds, and each hit is still trimmed to the doors the searcher holds.
    id: 'search.read',
    module: 'admin',
    view: 'decide',
    section: 'Decide',
    what: 'Jump to anything: find a sponsor, a person or a family by name — only what I may open.',
    rowScope: 'own',
    placed: 'The search box on every signed-in screen (GET /search)',
    askRole: 'director',
  }),
  C({
    id: 'admin.audit.read',
    module: 'admin',
    view: 'admin',
    section: 'System',
    what: 'Who did what.',
    rowScope: 'all',
    placed: 'Money → Close · Report → Journal → Who did what (/admin/audit for the platform rows)',
    askRole: 'director',
  }),
  C({
    id: 'admin.integrations.view',
    module: 'admin',
    view: 'admin',
    section: 'System',
    what: 'Connection status. Never a key.',
    rowScope: 'all',
    placed: 'Festival Admin → System → Integrations (People Part 3)',
    askRole: 'director',
  }),
  // ---- PLATFORM (the person who runs the platform — canvas defect #31; ruling 91) ---------------
  C({
    id: 'platform.integrations.manage',
    module: 'admin',
    view: 'admin',
    section: 'System',
    what: 'Connect, rotate and disconnect an integration (Square, Microsoft, the bucket, the error tracker). Never a key on screen.',
    rowScope: 'all',
    placed: 'Festival Admin → System → Integrations (People Part 3)',
    askRole: 'platform_admin',
  }),
  C({
    id: 'platform.signin.rules',
    module: 'admin',
    view: 'admin',
    section: 'System',
    what: 'The sign-in rules: lanes, rate limits, session length, which roles need a second factor.',
    rowScope: 'all',
    placed: 'Festival Admin → System → Sign-in rules (People Part 3)',
    askRole: 'platform_admin',
  }),
  C({
    id: 'platform.devices.manage',
    module: 'admin',
    view: 'admin',
    section: 'Platform',
    what: 'Enroll and revoke any enrolled device across the platform.',
    rowScope: 'all',
    askRole: 'platform_admin',
    placed: 'Ruling 91’s platform set; its screen is canvas defect #70',
  }),
  C({
    id: 'platform.roles.templates',
    module: 'admin',
    view: 'admin',
    section: 'Platform',
    what: 'Role templates and grant overrides — what a role holds. Granter ≠ grantee still.',
    rowScope: 'all',
    askRole: 'platform_admin',
    placed: 'Ruling 91’s platform set; its screen is canvas defect #71',
  }),
  C({
    id: 'platform.settings',
    module: 'admin',
    view: 'admin',
    section: 'Platform',
    what: 'System settings: the festival clock, backups, the document store, the worker.',
    rowScope: 'all',
    askRole: 'platform_admin',
    placed: 'Ruling 91’s platform set; its screen is canvas defect #72',
  }),
  C({
    id: 'platform.break_glass',
    module: 'admin',
    view: 'admin',
    section: 'Platform',
    what: 'Break-glass configuration: who may, for how long, and what a director reads the next morning.',
    rowScope: 'all',
    askRole: 'platform_admin',
    placed:
      'Ruling 91’s platform set; its screen is canvas defect #73 (docs/runbooks/break-glass.md)',
  }),
  C({
    id: 'admin.access_issue',
    module: 'admin',
    view: 'admin',
    section: 'People & roles',
    what: 'The can’t-get-in worklist and its four one-tap fixes.',
    rowScope: 'all',
    placed: 'Festival Admin → System → Access issues (defect #59)',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'meeting.record',
    module: 'admin',
    view: 'admin',
    section: 'Meetings',
    what: 'Record a decision, assign an owner, mark superseded.',
    rowScope: 'all',
    placed: 'Festival Admin → Meetings → Meetings (People Part 3)',
    askRole: 'volunteer_chair',
  }),

  // ---- PUBLIC (magic link) ---------------------------------------------------------------
  C({
    // Doors in Part 1: the identity step every door shares. A door pass from a code or a link
    // we sent stands for the one person and one household it verified, and nothing else.
    id: 'public.door',
    module: 'people',
    view: 'public',
    section: 'The identity step',
    what: 'Come in by a code or a link we sent: declare my family’s year, sign up as a sponsor. One person, one household, never the record.',
    rowScope: 'own',
    askRole: 'volunteer_chair',
    placed:
      'The public site → /who, /family/year, /sponsor/signup (Doors in Part 1; Contracts card “Doors in”)',
  }),
  C({
    id: 'public.signup',
    module: 'volunteers',
    view: 'public',
    section: 'Volunteer door',
    what: 'Sign up for a shift; confirm my address.',
    rowScope: 'own',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'portal.myshifts',
    module: 'volunteers',
    view: 'public',
    section: 'My shifts',
    what: 'See and cancel my shifts before the cut-off.',
    rowScope: 'own',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'family.portal.view',
    module: 'volunteers',
    view: 'public',
    section: 'Family page',
    what: 'My family’s year, the sentence, the statement, an appeal.',
    rowScope: 'own',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'portal.sponsor',
    module: 'marketing',
    view: 'public',
    section: 'Sponsor door',
    what: 'My own page: the pledge and what is paid, the receipt or the invoice, the agreement, my logo, where my name appears, and renewing for next year (Sponsors Part 5). One organization, one year, through the link we sent.',
    rowScope: 'own',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'portal.vendor',
    module: 'money',
    view: 'public',
    section: 'Vendor door',
    what: 'A vendor’s own page: our orders to them, what they have sent us, what we have paid and by which check number, our certificate of insurance, W-9 and sales-tax exemption, and somewhere to send an invoice or a price (Money Part 4). No account — the link in the order’s email is the door. One organization, one year.',
    rowScope: 'own',
    askRole: 'finance_director',
  }),
  C({
    id: 'public.order',
    module: 'commerce',
    view: 'public',
    section: 'Presale door',
    what: 'Build a basket and pay once.',
    rowScope: 'own',
    askRole: 'volunteer_chair',
  }),
  C({
    id: 'public.donate',
    module: 'commerce',
    view: 'public',
    section: 'Presale door',
    what: 'Give a gift and receive the tax line.',
    rowScope: 'own',
    askRole: 'finance_director',
  }),

  // ---- SYSTEM (the worker, the clock, the feeds) -----------------------------------------
  C({
    id: 'system.clock',
    module: 'admin',
    view: 'admin',
    section: 'System',
    what: 'Timed transitions: expiries, reminders, overdue, 2 AM.',
    rowScope: 'system',
    askRole: 'director',
  }),
  C({
    id: 'system.engine',
    module: 'admin',
    view: 'admin',
    section: 'System',
    what: 'A figure computed by its one engine.',
    rowScope: 'system',
    askRole: 'director',
  }),
  C({
    id: 'system.provider',
    module: 'comms',
    view: 'admin',
    section: 'System',
    what: 'Delivery truth from the email, text or push provider.',
    rowScope: 'system',
    askRole: 'director',
  }),
  C({
    id: 'system.feed',
    module: 'festival_ops',
    view: 'admin',
    section: 'System',
    what: 'The lightning feed, the Square pull, the Ramp feed, a screen heartbeat.',
    rowScope: 'system',
    askRole: 'director',
  }),
];

export const CAPABILITY_IDS = new Set(CAPABILITIES.map((c) => c.id));
export type CapabilityId = (typeof CAPABILITIES)[number]['id'];

export function capability(id: string): Capability {
  const c = CAPABILITIES.find((x) => x.id === id);
  if (!c) throw new Error(`unknown capability '${id}' — add it to the registry first`);
  return c;
}
